import { useMemo } from 'react';
import { useAppSelector } from '../redux/store/hooks';

export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  DOCTOR = 'doctor',
  NURSE = 'nurse',
  RECEPTIONIST = 'receptionist',
  CASHIER = 'cashier',
  LAB_TECHNICIAN = 'lab_technician',
  RADIOLOGIST = 'radiologist',
  PHARMACIST = 'pharmacist',
  HR = 'hr',
  STORE_MANAGER = 'store_manager',
  PATIENT = 'patient',
}

/**
 * System roles that may be assigned through the regular Employee / Doctor /
 * User management screens. SUPER_ADMIN is deliberately excluded — it is
 * reserved for the master developer account and can only be managed from the
 * Developer Console (`/settings/developer`). The backend enforces the same
 * rule by stripping SUPER_ADMIN (and ADMIN) from these requests.
 */
export const ASSIGNABLE_SYSTEM_ROLES: string[] = Object.values(UserRole).filter(
  (role) => role !== UserRole.SUPER_ADMIN && role !== UserRole.ADMIN,
);

/**
 * Route access granted by the built-in roles. Kept for backward compatibility:
 * a user whose account has one of these roles keeps exactly the access they had
 * before custom roles were introduced.
 *
 * These defaults only apply to accounts that have **no** dashboard custom role.
 * Once a dashboard custom role is assigned, its permissions become the single
 * source of truth (see `hasDashboardCustomRoles`), so a front-desk system role
 * no longer keeps the Billing menu visible after Billing is removed from the role.
 */
export const ROLE_PERMISSIONS: Record<string, string[]> = {
  [UserRole.SUPER_ADMIN]: ['*'],
  [UserRole.ADMIN]: ['*'],
  [UserRole.DOCTOR]: ['/', '/patients', '/patients/trash', '/patients/new', '/patients/categories', '/patients/categories/trash', '/patients/clinical-alerts', '/opd', '/appointments', '/appointments/new', '/opd/diagnostic-receipts', '/opd/diagnostic-receipts/new', '/ipd', '/diagnostics', '/administration/orders', '/pharmacy', '/settings', '/settings/*'],
  [UserRole.NURSE]: ['/', '/patients', '/patients/trash', '/patients/categories', '/patients/categories/trash', '/patients/clinical-alerts', '/ipd', '/diagnostics', '/administration/orders', '/pharmacy', '/ipd/vitals'],
  // Finance → Risk Fund is deliberately **not** part of any built-in role:
  // writing off a receivable is an explicit grant. The ledger (`/billing/risk-fund`)
  // is unlocked only by the `billing:risk-fund:*` permission family below.
  [UserRole.RECEPTIONIST]: ['/', '/patients', '/patients/trash', '/patients/new', '/patients/categories', '/patients/categories/trash', '/opd', '/appointments', '/appointments/new', '/opd/diagnostic-receipts', '/opd/diagnostic-receipts/new', '/billing', '/billing/invoices', '/billing/payments', '/billing/cashbook'],
  [UserRole.CASHIER]: ['/', '/opd', '/opd/diagnostic-receipts', '/opd/diagnostic-receipts/new', '/billing', '/billing/invoices', '/billing/payments', '/billing/cashbook', '/billing/revenue-settlements', '/billing/payouts', '/billing/referral-payouts', '/billing/expenses', '/billing/expenses/new', '/billing/expenses/by-category', '/billing/expenses/categories', '/billing/expenses/vendors', '/billing/assets/categories', '/billing/assets'],
  [UserRole.LAB_TECHNICIAN]: ['/', '/diagnostics', '/diagnostics/*', '/diagnostics/collection', '/lab/receiving', '/lab/workbench', '/administration/orders'],
  [UserRole.RADIOLOGIST]: ['/', '/diagnostics', '/administration/orders'],
  [UserRole.PHARMACIST]: ['/', '/pharmacy', '/pharmacy/*'],
  [UserRole.HR]: ['/', '/hr', '/hr/salary-advances', '/hr/payroll-policy', '/duty-roster', '/employees', '/employees/trash', '/employees/new', '/employees/:id', '/employees/:id/edit', '/doctors', '/doctors/trash', '/doctors/new', '/doctors/:id', '/doctors/:id/edit', '/referrals', '/referrals/registry', '/referrals/trash', '/departments', '/departments/trash', '/rooms', '/designations', '/specializations'],
  [UserRole.STORE_MANAGER]: ['/'],
  [UserRole.PATIENT]: ['/'],
};

/**
 * Maps permission-code prefixes to the navigation routes they unlock. This is
 * what lets permissions granted through custom roles — the roles managed on the
 * Roles & Permissions page — control route access and sidebar visibility,
 * instead of only gating in-page actions.
 *
 * Rules are matched by the *longest* prefix, so a granular permission such as
 * `diagnostics:specimen:collect` only unlocks the specimen workflow, while the
 * broader `diagnostics:read` unlocks the diagnostics configuration pages.
 * This mirrors how the backend guards each endpoint with `@Permissions(...)`.
 *
 * Keep this in sync with `routes/index.tsx` (the `ProtectedRoute path` props),
 * the sidebar nav in `components/layout/sidebar.tsx`, and the permission
 * catalog in `backend/src/modules/permissions/permissions.service.ts`.
 */
export const PERMISSION_ROUTE_RULES: { prefix: string; routes: string[] }[] = [
  // ── Backdate actions ──
  // In-page capabilities, not page unlockers: they let a role choose the date
  // on a create form, so they intentionally map to no routes of their own.
  { prefix: 'diagnostics:receipt:backdate', routes: [] },
  { prefix: 'diagnostics:receipt:override_base_discount', routes: [] },
  { prefix: 'ipd:admission:backdate', routes: [] },
  { prefix: 'expenses:backdate', routes: [] },
  { prefix: 'billing:invoice:backdate', routes: [] },
  // Lifts the seven-day billing history window on screens the role can already
  // reach; on its own it unlocks no page (see `billingAccess.ts`).
  { prefix: 'billing:history', routes: [] },
  // ── Diagnostics sub-resources (more specific than the `diagnostics` rule) ──
  {
    prefix: 'diagnostics:receipt:delete',
    routes: ['/opd/diagnostic-receipts/trash'],
  },
  {
    prefix: 'diagnostics:receipt',
    routes: [
      '/diagnostics',
      '/administration/orders',
      '/opd',
      '/opd/diagnostic-receipts',
      '/opd/diagnostic-receipts/new',
    ],
  },
  {
    prefix: 'diagnostics:routing',
    routes: ['/diagnostics', '/administration/orders', '/administration/orders/trash'],
  },
  { prefix: 'diagnostics:specimen_config', routes: ['/administration/specimen-types'] },
  {
    prefix: 'diagnostics:result',
    routes: ['/lab/workbench'],
  },
  {
    prefix: 'diagnostics:specimen',
    routes: ['/diagnostics', '/administration/orders', '/diagnostics/collection', '/lab/receiving', '/lab/workbench'],
  },
  // ── Diagnostics module (configuration) ──
  // `diagnostics:read` only guards the master-data endpoints on the backend, so
  // it maps to the configuration pages — not the receipt/routing/specimen flows.
  {
    prefix: 'diagnostics',
    routes: [
      '/administration/diagnostic-tests',
      '/administration/diagnostic-tests/trash',
      '/administration/diagnostic-accessories',
      '/administration/diagnostic-categories',
      '/administration/specimen-types',
    ],
  },

  // ── Other modules ──
  { prefix: 'dashboard', routes: ['/'] },
  {
    prefix: 'patients',
    routes: [
      '/patients',
      '/patients/trash',
      '/patients/new',
      '/patients/clinical-alerts',
      '/patients/categories',
      '/patients/categories/trash',
    ],
  },
  {
    prefix: 'opd',
    routes: ['/opd', '/opd/diagnostic-receipts', '/opd/diagnostic-receipts/new'],
  },
  // Appointments are their own screen with their own permission module, matching
  // the backend `appointments:*` guards.
  { prefix: 'appointments', routes: ['/appointments', '/appointments/new', '/appointments/:id/edit'] },
  { prefix: 'ipd', routes: ['/ipd', '/ipd/occupancy'] },
  {
    prefix: 'vitals',
    routes: ['/ipd/vitals'],
  },
  { prefix: 'wards', routes: ['/wards', '/beds'] },
  // Billing is page-scoped: each granular `billing:<page>:*` code unlocks only
  // its own page. The broad `billing` prefix below still unlocks the other
  // billing pages so existing custom roles keep working unchanged — except the
  // Risk Fund ledger, which has its own explicit rule below.
  { prefix: 'billing:overview', routes: ['/billing'] },
  { prefix: 'billing:top_sheet', routes: ['/billing/top-sheet'] },
  {
    prefix: 'billing:invoice',
    routes: ['/billing/invoices', '/billing/invoices/new', '/billing/invoices/trash', '/billing/invoices/:id/edit'],
  },
  { prefix: 'billing:payment', routes: ['/billing/payments'] },
  { prefix: 'billing:cashbook', routes: ['/billing/cashbook', '/billing/cashbook/trash'] },
  // The Risk Fund ledger is granted **only** by its own permission family
  // (`billing:risk-fund:read|manage|delete`) — it is intentionally absent from
  // the broad `billing` rule below so `billing:read` (or any other billing
  // screen grant) can never reveal the write-off ledger or its API. `manage`
  // (write-off) and `delete` (reversal) are in-page actions on this screen, so
  // the whole family unlocks the page; the buttons are gated separately by
  // `hasPermission`.
  { prefix: 'billing:risk-fund', routes: ['/billing/risk-fund'] },
  // Supplier payables are a Payouts slice owned by Billing: paying a supplier
  // is a finance action, even though the invoice/GRN data belongs to Pharmacy.
  // A role holding only these codes sees the Payables screen and nothing else
  // in Pharmacy.
  {
    prefix: 'billing:payout:supplier',
    routes: ['/pharmacy/payables'],
  },
  // Operating-expense payables live on the Expenses ledger under Billing.
  {
    prefix: 'billing:payout:vendor',
    routes: ['/billing/expenses'],
  },
  // Finance → Payouts is Billing-scoped. `billing:settlement:*` is the single
  // permission family for payouts; `/billing/revenue-settlements` and
  // `/billing/referral-payouts` are legacy URLs that redirect into it.
  {
    prefix: 'billing:settlement',
    routes: [
      '/billing/payouts',
      '/billing/revenue-settlements',
      '/billing/referral-payouts',
    ],
  },
  {
    prefix: 'billing',
    routes: [
      '/billing',
      '/billing/top-sheet',
      '/billing/invoices',
      '/billing/invoices/new',
      '/billing/invoices/trash',
      '/billing/invoices/:id/edit',
      '/billing/payments',
      '/billing/cashbook',
      '/billing/cashbook/trash',
      '/billing/payouts',
      '/billing/revenue-settlements',
    ],
  },
  // Expense master data (categories / vendors) can be granted on its own, but
  // the broad `assets`/`expenses` prefixes also still unlock them.
  { prefix: 'expenses:category', routes: ['/billing/expenses/categories'] },
  {
    prefix: 'expenses:vendor',
    routes: ['/billing/expenses/vendors', '/billing/expenses/vendors/new', '/billing/expenses/vendors/:id/edit'],
  },
  { prefix: 'assets:invoice', routes: ['/billing/assets'] },
  // Asset invoices live on the Assets page (the old `/billing/assets/invoices`
  // screen redirects there), and expense master data is owned by the
  // `expenses:*` permissions below — so `assets:*` only ever reveals the Assets
  // menu and never the Billing or Expenses menu.
  { prefix: 'assets', routes: ['/billing/assets', '/billing/assets/categories'] },
  {
    prefix: 'expenses',
    routes: [
      '/billing/expenses',
      '/billing/expenses/new',
      '/billing/expenses/:id/edit',
      '/billing/expenses/by-category',
      '/billing/expenses/categories',
      '/billing/expenses/vendors',
      '/billing/expenses/vendors/new',
      '/billing/expenses/vendors/:id/edit',
    ],
  },
  { prefix: 'services', routes: ['/administration/services', '/administration/services/trash', '/administration/service-types'] },
  {
    prefix: 'pharmacy',
    routes: [
      '/pharmacy',
      '/pharmacy/drugs',
      '/pharmacy/ledger',
      '/pharmacy/purchases',
      '/pharmacy/receipts',
      '/pharmacy/suppliers',
      '/pharmacy/payables',
      '/pharmacy/categories',
      '/pharmacy/generics',
      '/pharmacy/companies',
      '/pharmacy/racks',
    ],
  },
  { prefix: 'hr', routes: ['/hr', '/hr/salary-advances', '/hr/payroll-policy', '/employees', '/employees/trash', '/duty-roster', '/designations'] },
  { prefix: 'doctors', routes: ['/doctors', '/doctors/trash'] },
  // Referral partners are managed under HR Mgmt. and gated by `referrals:*`.
  // The registry lives in the `/referrals` workspace. Paying partners is a
  // Billing concern — it is granted by `billing:settlement:*` and happens in
  // the Finance → Payouts workspace (`/billing/payouts`), never here.
  {
    prefix: 'referrals',
    routes: ['/referrals', '/referrals/registry', '/referrals/trash', '/outside-referrals'],
  },
  { prefix: 'departments', routes: ['/departments', '/departments/trash', '/rooms'] },
  { prefix: 'specializations', routes: ['/specializations'] },
  {
    prefix: 'users',
    routes: ['/users', '/users/:id', '/users/:id/edit', '/users/:id/roles'],
  },
  {
    prefix: 'roles',
    routes: ['/roles', '/roles/new', '/roles/:id/edit', '/roles/:id/permissions'],
  },
  { prefix: 'permissions', routes: ['/roles'] },
  { prefix: 'settings', routes: ['/settings', '/settings/general', '/settings/devices', '/settings/backup'] },
  { prefix: 'devices', routes: ['/settings/devices'] },
  { prefix: 'backup', routes: ['/settings/backup'] },
  // Reports is permission-driven: no built-in role grants it (except the
  // admin wildcard). Access comes from a custom role holding `reports:read`,
  // matching the backend `@Permissions('reports:read')` guard.
  { prefix: 'reports', routes: ['/reports'] },
  { prefix: 'audit', routes: ['/audit'] },
  { prefix: 'retention', routes: ['/audit'] },
  { prefix: 'notifications', routes: ['/notifications'] },
  { prefix: 'visits', routes: ['/visits'] },
  { prefix: 'website', routes: ['/website', '/website/services', '/website/blogs', '/website/gallery', '/website/testimonials', '/website/plans', '/website/taxonomy', '/website/contact'] },
];

/** Every navigable route, returned when a user holds the `*` wildcard. */
const ALL_APP_PATHS = [
  '/',
  '/patients',
  '/patients/trash',
  '/patients/new',
  '/patients/:id',
  '/patients/:id/edit',
  '/patients/categories',
  '/patients/categories/trash',
  '/patients/clinical-alerts',
  '/opd',
  '/appointments',
  '/appointments/new',
  '/appointments/:id/edit',
  '/opd/diagnostic-receipts',
  '/opd/diagnostic-receipts/new',
  '/opd/diagnostic-receipts/trash',
  '/opd/diagnostic-receipts/:id',
  '/ipd',
  '/ipd/new',
  '/ipd/occupancy',
  '/ipd/admissions/:id',
  '/wards',
  '/beds',
  '/billing',
  '/billing/top-sheet',
  '/billing/risk-fund',
  '/billing/invoices',
  '/billing/payments',
  '/billing/cashbook',
  '/billing/cashbook/trash',
  '/billing/payouts',
  '/billing/revenue-settlements',
  '/billing/referral-payouts',
  '/billing/assets',
  '/billing/assets/categories',
  '/billing/assets/new',
  '/billing/assets/invoices',
  '/billing/assets/invoices/:id',
  '/billing/expenses/categories',
  '/billing/expenses/vendors',
  '/billing/expenses/vendors/new',
  '/billing/expenses/vendors/:id/edit',
  '/billing/expenses',
  '/billing/expenses/new',
  '/billing/expenses/:id/edit',
  '/billing/expenses/by-category',
  '/diagnostics',
  '/diagnostics/*',
  '/diagnostics/:departmentId',
  '/diagnostics/collection',
  '/diagnostics/receipts/:id',
  '/diagnostics/routing/:routingId',
  '/lab/receiving',
  '/lab/workbench',
  '/pharmacy',
  '/pharmacy/new',
  '/pharmacy/sales',
  '/pharmacy/drugs',
  '/pharmacy/drugs/new',
  '/pharmacy/drugs/:id/edit',
  '/pharmacy/:id/edit',
  '/pharmacy/categories',
  '/pharmacy/generics',
  '/pharmacy/companies',
  '/pharmacy/racks',
  '/pharmacy/suppliers',
  '/pharmacy/purchases',
  '/pharmacy/purchases/new',
  '/pharmacy/purchases/:id',
  '/pharmacy/purchases/:id/receive',
  '/pharmacy/receipts',
  '/pharmacy/receipts/:id',
  '/pharmacy/stock',
  '/pharmacy/ledger',
  '/pharmacy/payables',
  '/pharmacy/trash',
  '/pharmacy/purchasing-trash',
  '/ipd/vitals',
  '/ipd/vitals/new',
  '/visits',
  '/visits/new',
  '/hr',
  '/hr/leave/new',
  '/hr/payroll/new',
  '/hr/payroll/:id/edit',
  '/hr/salary-advances',
  '/hr/payroll-policy',
  '/duty-roster',
  '/employees',
  '/employees/trash',
  '/employees/new',
  '/employees/:id',
  '/employees/:id/edit',
  '/doctors',
  '/doctors/trash',
  '/doctors/new',
  '/doctors/:id',
  '/doctors/:id/edit',
  '/referrals',
  '/referrals/registry',
  '/referrals/trash',
  '/outside-referrals',
  '/departments',
  '/departments/trash',
  '/rooms',
  '/designations',
  '/specializations',
  '/users',
  '/users/new',
  '/users/:id',
  '/users/:id/edit',
  '/users/:id/roles',
  '/roles',
  '/roles/new',
  '/roles/:id/edit',
  '/roles/:id/permissions',
  '/audit',
  '/notifications',
  '/reports',
  '/settings',
  '/settings/general',
  '/settings/devices',
  '/settings/backup',
  '/settings/developer',
  '/administration',
  '/administration/orders',
  '/administration/orders/trash',
  '/administration/services',
  '/administration/services/trash',
  '/administration/service-types',
  '/administration/diagnostic-tests',
  '/administration/diagnostic-tests/trash',
  '/administration/diagnostic-tests/new',
  '/administration/diagnostic-tests/:id/edit',
  '/administration/diagnostic-accessories',
  '/administration/diagnostic-categories',
  '/administration/specimen-types',
  '/website',
  '/website/services',
  '/website/services/new',
  '/website/services/:id/edit',
  '/website/blogs',
  '/website/blogs/new',
  '/website/blogs/:id/edit',
  '/website/gallery',
  '/website/testimonials',
  '/website/plans',
  '/website/taxonomy',
  '/website/contact',
];

function matchesPermission(required: string, userPermissions: Set<string>): boolean {
  if (userPermissions.has(required)) return true;

  const reqParts = required.split(':');
  for (const userPerm of userPermissions) {
    const userParts = userPerm.split(':');
    if (userParts.length !== reqParts.length) continue;

    let matches = true;
    for (let i = 0; i < userParts.length; i++) {
      // A `*` in either the granted or the required permission acts as a
      // wildcard segment, mirroring the backend PermissionsGuard (e.g.
      // `patients:*` satisfies `patients:create`).
      if (userParts[i] !== '*' && reqParts[i] !== '*' && userParts[i] !== reqParts[i]) {
        matches = false;
        break;
      }
    }
    if (matches) return true;
  }
  return false;
}

/**
 * Resolves the app routes unlocked by a permission code via its longest
 * matching prefix. Exported so the Roles → Permission Catalog UI can show which
 * pages (e.g. `/billing/cashbook`) each permission grants.
 */
export function resolvePermissionRoutes(code: string): string[] {
  let best: { prefix: string; routes: string[] } | undefined;
  for (const rule of PERMISSION_ROUTE_RULES) {
    const matches = code === rule.prefix || code.startsWith(`${rule.prefix}:`);
    if (matches && (!best || rule.prefix.length > best.prefix.length)) {
      best = rule;
    }
  }
  return best?.routes ?? [];
}

/**
 * True when `path` equals an entry or falls under a `prefix/*` entry.
 *
 * Custom-role routes are intentionally matched exactly (no implicit `/*`) so a
 * parent path such as `/administration/orders` does not also grant its
 * `/administration/orders/trash` child. Descendants are listed explicitly.
 */
function pathMatchesAny(path: string, allowedPaths: Iterable<string>): boolean {
  for (const allowed of allowedPaths) {
    if (allowed === path) return true;
    if (allowed.endsWith('/*') && path.startsWith(allowed.slice(0, -1))) return true;
  }
  return false;
}

/** True when `path` is a parent of an allowed route (e.g. `/administration`). */
function isParentOfAllowed(path: string, allowedPaths: Iterable<string>): boolean {
  const prefix = `${path}/`;
  for (const allowed of allowedPaths) {
    if (allowed.startsWith(prefix)) return true;
  }
  return false;
}

export function usePermissions() {
  const { user } = useAppSelector((state) => state.auth);

  const userRoles = user?.roles || [];

  const userPermissions = useMemo(() => {
    const perms = new Set<string>();
    if (user?.customRoles && Array.isArray(user.customRoles)) {
      for (const role of user.customRoles) {
        // Website-only roles tag blog authors; their permissions must never
        // unlock dashboard routes (mirrors the backend PermissionsGuard).
        if ((role as any).isWebsiteOnly) continue;
        if (role.permissions && Array.isArray(role.permissions)) {
          for (const perm of role.permissions) {
            if (perm.code) perms.add(perm.code);
          }
        }
      }
    }
    return perms;
  }, [user?.customRoles]);

  const isPrivileged =
    userRoles.includes(UserRole.SUPER_ADMIN) || userRoles.includes(UserRole.ADMIN);

  const hasWildcardPermission = userPermissions.has('*');

  /**
   * True when the account has at least one dashboard (non-website) custom role.
   *
   * Once an account has a dashboard custom role, that role's permissions become
   * the single source of truth for route/sidebar access — the built-in system
   * role's default route map (e.g. Receptionist → Billing) is ignored. Without
   * this, assigning a restrictive custom role would not remove the screens the
   * system role happens to grant, so the Billing menu kept showing for front-desk
   * staff even when Billing was not granted.
   */
  const hasDashboardCustomRoles = useMemo(
    () => (user?.customRoles || []).some((role) => !role.isWebsiteOnly),
    [user?.customRoles],
  );

  /** Routes unlocked by the user's custom-role permissions. `null` = wildcard. */
  const customRolePaths = useMemo(() => {
    if (userPermissions.has('*')) return null;
    const paths = new Set<string>();
    for (const code of userPermissions) {
      for (const route of resolvePermissionRoutes(code)) {
        paths.add(route);
      }
    }
    return paths;
  }, [userPermissions]);

  const hasRole = (role: string): boolean => {
    return userRoles.includes(role) || userRoles.includes(UserRole.SUPER_ADMIN);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some((role) => hasRole(role));
  };

  const hasPermission = (perm: string): boolean => {
    if (isPrivileged) return true;
    return matchesPermission(perm, userPermissions);
  };

  const hasAnyPermission = (perms: string[]): boolean => {
    if (isPrivileged) return true;
    return perms.some((p) => matchesPermission(p, userPermissions));
  };

  const hasAllPermissions = (perms: string[]): boolean => {
    if (isPrivileged) return true;
    return perms.every((p) => matchesPermission(p, userPermissions));
  };

  const hasAccess = (path: string): boolean => {
    if (isPrivileged) return true;
    if (hasWildcardPermission) return true;

    // Custom roles take precedence over the built-in role route map. When a
    // dashboard custom role is assigned, only its mapped routes grant access,
    // so the built-in system role no longer leaks extra screens.
    if (hasDashboardCustomRoles) {
      if (customRolePaths && customRolePaths.size > 0) {
        if (pathMatchesAny(path, customRolePaths)) return true;
        if (isParentOfAllowed(path, customRolePaths)) return true;
      }
      return false;
    }

    // Built-in role route map (backward compatible) for accounts that have no
    // dashboard custom roles.
    for (const role of userRoles) {
      const allowedPaths = ROLE_PERMISSIONS[role] || [];
      if (allowedPaths.includes('*')) return true;
      if (pathMatchesAny(path, allowedPaths)) return true;
      // Parent layout routes (e.g. `/administration`) are reachable when any
      // of their descendants is allowed.
      if (isParentOfAllowed(path, allowedPaths)) return true;
    }

    return false;
  };

  const getAllowedPaths = (): string[] => {
    // Custom roles are the single source of truth once assigned: return only the
    // routes their permissions unlock (plus the always-visible dashboard), so a
    // system role's default screens (e.g. Billing for a receptionist) do not leak
    // back into the sidebar.
    if (hasDashboardCustomRoles) {
      if (hasWildcardPermission) return [...ALL_APP_PATHS];
      const customPaths = new Set<string>();
      customRolePaths?.forEach((p) => customPaths.add(p));
      // The dashboard is the unprotected landing page — always keep it visible.
      customPaths.add('/');
      return Array.from(customPaths);
    }

    const paths = new Set<string>();
    let hasWildcard = false;

    for (const role of userRoles) {
      const allowedPaths = ROLE_PERMISSIONS[role] || [];
      if (allowedPaths.includes('*')) {
        hasWildcard = true;
      }
      allowedPaths.forEach((p) => paths.add(p));
    }

    if (hasWildcardPermission) hasWildcard = true;
    if (customRolePaths) customRolePaths.forEach((p) => paths.add(p));

    // If user has wildcard permission, return all possible application paths
    if (hasWildcard) {
      return [...ALL_APP_PATHS];
    }

    // The dashboard is the unprotected landing page — always keep it visible.
    paths.add('/');

    return Array.from(paths);
  };

  return {
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasAccess,
    getAllowedPaths,
    userRoles,
    userPermissions,
  };
}